// Credential entry (ADR 0015): write-only after save. A credential is typed into a masked field (or
// named as a reference to the secret manager); once saved, the page only ever shows that one is saved
// (masked) and offers to replace it. No route returns a held credential's value, so there is none to show.
import * as React from 'react';

/** What the person did with a credential field. */
export type SecretEntry =
  /** Nothing: the saved credential stays as it is. */
  | { mode: 'keep' }
  /** A value typed into the masked field: the platform holds it (a KMS envelope). */
  | { mode: 'value'; value: string }
  /** A reference to the secret manager (`projects/acme/pg-password`). */
  | { mode: 'ref'; ref: string };

export const KEEP: SecretEntry = { mode: 'keep' };

/** A stored credential as a config carries it: `{ ref }` (a held credential or a secret-manager name). */
export type SavedSecret = { ref: string } | null;

export const HELD_PREFIX = 'held:';

/** The saved reference in a stored config value, if any (never a value: configs hold none). */
export function savedSecret(v: unknown): SavedSecret {
  return v && typeof v === 'object' && typeof (v as { ref?: unknown }).ref === 'string'
    ? { ref: (v as { ref: string }).ref }
    : null;
}

/** A connection field that takes a credential: typed `password`, or named like one (a service-account JSON). */
export function isSecretField(f: { name: string; type: string }): boolean {
  return (
    f.type === 'password' ||
    /(^|_)(password|passphrase|secret|token|api_?key|private_?key|credentials(_json)?|connection_?string)$/i.test(
      f.name.replace(/([a-z])([A-Z])/g, '$1_$2'),
    )
  );
}

/**
 * The config value to send for an entry: `{ value }` (held on save), `{ ref }`, the saved one
 * unchanged (keep, or an empty replacement), or undefined (nothing saved, nothing typed).
 */
export function secretConfigValue(entry: SecretEntry | undefined, saved: SavedSecret): unknown {
  if (entry?.mode === 'value' && entry.value.length > 0) return { value: entry.value };
  if (entry?.mode === 'ref' && entry.ref.trim()) return { ref: entry.ref.trim() };
  return saved ?? undefined;
}

const INPUT =
  'w-full h-9 px-3 rounded-[5px] border border-[var(--input-border)] bg-page-bg text-page-text text-sm outline-none focus:border-nav-active';
const LINK =
  'bg-transparent border-0 p-0 text-xs font-medium text-nav-active cursor-pointer hover:underline disabled:opacity-50';

/**
 * A write-only credential field. Unsaved: a masked input (or, when references are allowed, a
 * reference input). Saved: "•••••••• Saved" and Replace; the value is never shown back.
 */
export function SecretField({
  saved,
  entry,
  onChange,
  testId,
  placeholder,
  allowRef = true,
  inputClassName = INPUT,
  label,
}: {
  saved: SavedSecret;
  entry: SecretEntry | undefined;
  onChange: (e: SecretEntry) => void;
  testId: string;
  placeholder?: string;
  /** Offer "use a secret reference" (connections); the fleet's keys take a value or a reference field of their own. */
  allowRef?: boolean;
  inputClassName?: string;
  /** For the input's accessible name. */
  label?: string;
}) {
  const e = entry ?? (saved ? KEEP : { mode: 'value', value: '' });
  if (saved && e.mode === 'keep') {
    const held = saved.ref.startsWith(HELD_PREFIX);
    return (
      <div className="flex items-center gap-3 min-h-9" data-testid={`${testId}-saved`}>
        <span
          className="inline-flex items-center gap-2 h-9 px-3 rounded-[5px] border border-[var(--input-border)] bg-page-bg text-page-muted text-sm select-none flex-1 min-w-0"
          aria-label={label ? `${label}: saved` : 'saved credential'}
        >
          <span aria-hidden className="tracking-widest">
            ••••••••
          </span>
          <span className="text-xs truncate">
            {held ? 'Saved, held encrypted' : `Reference: ${saved.ref}`}
          </span>
        </span>
        <button
          type="button"
          className={LINK}
          onClick={() => onChange({ mode: 'value', value: '' })}
          data-testid={`${testId}-replace`}
        >
          Replace
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {e.mode === 'ref' ? (
        <input
          className={inputClassName}
          type="text"
          value={e.ref}
          placeholder="Secret reference, e.g. projects/acme/pg-password"
          aria-label={label ? `${label} (secret reference)` : undefined}
          onChange={(x) => onChange({ mode: 'ref', ref: x.target.value })}
          data-testid={testId}
          autoComplete="off"
        />
      ) : (
        <input
          className={inputClassName}
          type="password"
          value={e.mode === 'value' ? e.value : ''}
          placeholder={placeholder ?? (saved ? 'Type the new value' : '')}
          aria-label={label}
          onChange={(x) => onChange({ mode: 'value', value: x.target.value })}
          data-testid={testId}
          autoComplete="new-password"
          spellCheck={false}
        />
      )}
      <div className="flex items-center gap-3">
        {allowRef && (
          <button
            type="button"
            className={LINK}
            onClick={() =>
              onChange(e.mode === 'ref' ? { mode: 'value', value: '' } : { mode: 'ref', ref: '' })
            }
            data-testid={`${testId}-mode`}
          >
            {e.mode === 'ref' ? 'Type the value instead' : 'Use a secret reference instead'}
          </button>
        )}
        {saved && (
          <button
            type="button"
            className={LINK}
            onClick={() => onChange(KEEP)}
            data-testid={`${testId}-keep`}
          >
            Keep the saved one
          </button>
        )}
      </div>
    </div>
  );
}

/** The secret entries of a form, one per credential field, with a setter. */
export function useSecretEntries() {
  const [entries, setEntries] = React.useState<Record<string, SecretEntry>>({});
  const set = React.useCallback(
    (name: string, e: SecretEntry) => setEntries((s) => ({ ...s, [name]: e })),
    [],
  );
  const reset = React.useCallback(() => setEntries({}), []);
  return { entries, set, reset };
}
